import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import {
  Button,
  Card,
  Checkbox,
  LoadError,
  Loading,
  Notice,
  SelectField,
  TextField,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';

type Prefs = Schemas['AlertPreferences'];
const MIN_DOWNTIME = [0, 1, 2, 5, 10] as const;
const REMINDERS = [0, 6, 12, 24, 48] as const;

function Preferences() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const prefs = useQuery({
    queryKey: ['alert-preferences'],
    queryFn: () => unwrap(api.GET('/account/alert-preferences')),
  });
  const [draft, setDraft] = useState<Prefs>();
  useEffect(() => {
    if (prefs.data) setDraft(prefs.data);
  }, [prefs.data]);
  const save = useMutation({
    mutationFn: (body: Prefs) => unwrap(api.PUT('/account/alert-preferences', { body })),
    onSuccess: (data) => client.setQueryData(['alert-preferences'], data),
  });
  if (prefs.isPending || !draft)
    return prefs.isError ? (
      <LoadError message={errorMessage(prefs.error, t)} onRetry={() => void prefs.refetch()} />
    ) : (
      <Loading />
    );
  const set = <K extends keyof Prefs>(key: K, value: Prefs[K]) =>
    setDraft({ ...draft, [key]: value });
  return (
    <Card>
      <h2 className="mb-4 text-xl font-semibold">{t('notifications.prefsTitle')}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(draft);
        }}
        className="flex flex-col gap-4"
      >
        <fieldset className="flex flex-col gap-3">
          <legend className="sr-only">{t('notifications.prefsTitle')}</legend>
          <Checkbox
            label={t('notifications.downAlert')}
            checked={draft.downAlert}
            onChange={(e) => set('downAlert', e.target.checked)}
          />
          <Checkbox
            label={t('notifications.recoveryAlert')}
            checked={draft.recoveryAlert}
            onChange={(e) => set('recoveryAlert', e.target.checked)}
          />
          <Checkbox
            label={t('notifications.degradedAlert')}
            checked={draft.degradedAlert}
            onChange={(e) => set('degradedAlert', e.target.checked)}
          />
          <Checkbox
            label={t('notifications.goodNewsAlert')}
            checked={draft.goodNewsAlert}
            onChange={(e) => set('goodNewsAlert', e.target.checked)}
          />
          <Checkbox
            label={t('notifications.weeklyDigest')}
            checked={draft.weeklyDigest}
            onChange={(e) => set('weeklyDigest', e.target.checked)}
          />
          <Checkbox
            label={t('notifications.monthlyReport')}
            checked={draft.monthlyReport}
            onChange={(e) => set('monthlyReport', e.target.checked)}
          />
        </fieldset>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label={t('notifications.minDowntime')}
            value={draft.minDowntimeMinutes}
            onChange={(e) =>
              set('minDowntimeMinutes', Number(e.target.value) as Prefs['minDowntimeMinutes'])
            }
          >
            {MIN_DOWNTIME.map((m) => (
              <option key={m} value={m}>
                {t(`notifications.minDowntimeOptions.${m}`)}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t('notifications.reminder')}
            value={draft.downReminderHours}
            onChange={(e) =>
              set('downReminderHours', Number(e.target.value) as Prefs['downReminderHours'])
            }
          >
            {REMINDERS.map((h) => (
              <option key={h} value={h}>
                {t(`notifications.reminderOptions.${h}`)}
              </option>
            ))}
          </SelectField>
        </div>
        {save.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
        {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
        <div>
          <Button type="submit" busy={save.isPending}>
            {t('app.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Recipients() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const channels = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => unwrap(api.GET('/alert-channels')),
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['alert-channels'] });
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string }>();
  const add = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/alert-channels', { body: { type: 'email', target: email } })),
    onSuccess: (c) => {
      setMessage({ kind: 'success', text: t('notifications.added', { target: c.target }) });
      setEmail('');
      void refresh();
    },
    onError: (error) =>
      setMessage({
        kind: 'error',
        text:
          error instanceof ApiError && error.code === 'PLAN_LIMIT_REACHED'
            ? t('notifications.limitReached', { max: error.problem.limit?.max ?? 1 })
            : errorMessage(error, t),
      }),
  });
  const act = useMutation({
    mutationFn: async ({
      action,
      id,
    }: {
      action: 'test' | 'remove' | 'toggle';
      id: string;
      enabled?: boolean;
    }) => {
      if (action === 'test')
        return unwrap(
          api.POST('/alert-channels/{channelId}/test', { params: { path: { channelId: id } } }),
        );
      if (action === 'remove')
        return unwrap(
          api.DELETE('/alert-channels/{channelId}', { params: { path: { channelId: id } } }),
        );
      const current = channels.data?.items.find((c) => c.id === id);
      return unwrap(
        api.PATCH('/alert-channels/{channelId}', {
          params: { path: { channelId: id } },
          body: { enabled: !current?.enabled },
        }),
      );
    },
    onSuccess: (_d, v) => {
      const target = channels.data?.items.find((c) => c.id === v.id)?.target ?? '';
      if (v.action === 'test')
        setMessage({ kind: 'success', text: t('notifications.testSent', { target }) });
      void refresh();
    },
    onError: (error) => setMessage({ kind: 'error', text: errorMessage(error, t) }),
  });

  if (channels.isPending) return <Loading />;
  if (channels.isError)
    return (
      <LoadError
        message={errorMessage(channels.error, t)}
        onRetry={() => void channels.refetch()}
      />
    );
  const { items, limit } = channels.data;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setMessage(undefined);
    if (!email.trim()) return setMessage({ kind: 'error', text: t('errors.email') });
    add.mutate();
  };
  return (
    <Card>
      <h2 className="text-xl font-semibold">{t('notifications.recipientsTitle')}</h2>
      <p className="mt-1 text-slate-800">{t('notifications.recipientsIntro')}</p>
      <p className="mt-1 text-sm text-slate-700">
        {t('notifications.limit', { count: items.length, max: limit })}
      </p>
      <div aria-live="polite" className="mt-3">
        {message ? <Notice kind={message.kind}>{message.text}</Notice> : null}
      </div>
      <ul className="mt-4 flex flex-col divide-y divide-slate-200">
        {items.map((c) => (
          <li
            key={c.id}
            className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p className="break-all font-medium">
                {c.target}{' '}
                {c.isOwner ? (
                  <span className="text-slate-700">({t('notifications.you')})</span>
                ) : null}
              </p>
              <p className="text-sm text-slate-700">
                {c.verified ? t('notifications.confirmed') : t('notifications.pending')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Checkbox
                label={t('notifications.active')}
                checked={c.enabled}
                onChange={() => act.mutate({ action: 'toggle', id: c.id })}
              />
              {c.verified ? (
                <Button
                  variant="secondary"
                  onClick={() => act.mutate({ action: 'test', id: c.id })}
                >
                  {t('notifications.test')}
                </Button>
              ) : null}
              {c.isOwner ? null : (
                <Button
                  variant="danger"
                  onClick={() => {
                    if (window.confirm(t('notifications.removeConfirm', { target: c.target })))
                      act.mutate({ action: 'remove', id: c.id });
                  }}
                >
                  {t('notifications.remove')}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <form
        onSubmit={submit}
        noValidate
        className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-end"
      >
        <div className="flex-1">
          <TextField
            label={t('notifications.addEmail')}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <Button type="submit" busy={add.isPending}>
          {t('notifications.add')}
        </Button>
      </form>
    </Card>
  );
}

/** S9 / US-07. */
export function Notifications() {
  const { t } = useTranslation();
  return (
    <>
      <PageTitle>{t('notifications.title')}</PageTitle>
      <div className="flex flex-col gap-6">
        <Preferences />
        <Recipients />
      </div>
    </>
  );
}
